import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useState } from 'react';
import { Box, CircularProgress, Button, Typography, Stack, Autocomplete, TextField } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import AddButton from '../../../components/Admin/AddButton';
import AddTask from '../../../components/Admin/Tasks/AddTask';
import ModalWidget from '../../../components/ModalWidget/ModalWidget';
import AdminHeader from '../../../components/Admin/AdminHeader';
import { useGetTaskTemplatesQuery } from '../../../services/tasksSlice/tasksSlice';
import TaskCard from '../../../components/Admin/Tasks/TaskCard';
import {
  TaskPhase,
  TaskTemplate,
  TaskStatus,
} from '../../../../shared/types/Task';
import { useGetDepartmentsQuery } from '../../../services/departmentSlice/departmentSlice';
import SpinnerOverlay from '../../../../frontend/components/Spinner/Spinner';
import { useTheme } from '@mui/material/styles';

const filterTasks = (TaskTemplates: any, selectedTask: any, selectedDepartment: any) => {
  let result = TaskTemplates;
  
  // If both task and department are selected
  if (selectedTask && selectedDepartment) {
    // Check if the selected task belongs to the selected department
    if (selectedTask.departmentID === selectedDepartment.id) {
      return TaskTemplates?.filter((item: any) => item.id === selectedTask.id);
    }
    // Task doesn't belong to the department - return empty array
    return [];
  }
  
  // If only task is selected
  if (selectedTask) {
    return TaskTemplates?.filter((item: any) => item.id === selectedTask.id);
  }
  
  // If only department is selected
  if (selectedDepartment) {
    result = result?.filter((item: any) => item.departmentID === selectedDepartment.id);
  }
  
  return result;
};
const initTaskTemplate: TaskTemplate = {
  id: 0,
  createdAt: new Date().toString(),
  updatedAt: new Date().toString(),
  deletedAt: null,
  startedAt: null,
  completedAt: null,
  createdBy: new Date().toString(),
  taskTemplateID: 0,
  defaultAssignee: 'requestor',
  assignedUserID: '',
  defaultReviewerID: null,
  template: true,
  departmentID: 0,
  sepID: 0,
  phase: TaskPhase.initiate,
  status: TaskStatus.pending,
  enabled: false,
  review: true,
  locked: false,
  name: '',
  shortName: '',
  description: '',
  taskDataFields: [],
  parentTasks: [],
  stakeholderSummary:'',
  requestorSummary:'',
  groupName: '',
  groupId: null,
  taskAge: null,
  requestorSla: null,
  stakeholderSla: null,
  // Multi-department fields (default to false/empty array - safe for existing templates)
  isMultiDepartment: false,
  additionalDepartmentIDs: [],
};

const textFieldSx = {
  color: "#222222",
  width:  "100%",
  "& > p": {
    margin: "5px 0px",
    color: "#646669",
    fontSize: typographyTokens.fontSizes.xs,
  },
};

const AdminTasks = () => {
  const { data: TaskTemplates, isLoading } = useGetTaskTemplatesQuery();
  const { data: departments } = useGetDepartmentsQuery();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedTaskTemplate, setSelectedTaskTemplate] =
    useState(initTaskTemplate);
   //const [page, setPage] = useState<number>(0);
   const [open, setOpen] = React.useState(false);
   const [isFilter, setIsFilter] = useState(0);
   // Current selection state (for drawer UI)
   const [selectedTask, setSelectedTask] = useState<any>(null);
   const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
   // Applied filter state (persisted until reset)
   const [appliedTask, setAppliedTask] = useState<any>(null);
   const [appliedDepartment, setAppliedDepartment] = useState<any>(null);
   const [loadingData, setLoadingData] = useState<boolean>(false);
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };
  const theme = useTheme();

  const sortedTasks = [...(TaskTemplates || [])].sort((a: any, b: any) =>
    (a?.name || "").localeCompare(b?.name || "")
  );

  const sortedDepartments = [...(departments || [])].sort((a: any, b: any) =>
    (a?.name || "").localeCompare(b?.name || "")
  );

  const filteredList = isFilter ? filterTasks(TaskTemplates, appliedTask, appliedDepartment) : TaskTemplates
  const DrawerList = (
    <Box sx={{ width: 400, padding:'15px' }} role="presentation" >
      <Box sx={{display:'flex',flexDirection:'row',justifyContent:'space-between'}}>
      <Typography
              component="span"
              sx={{
                fontSize: typographyTokens.fontSizes.xl,
                fontWeight: typographyTokens.fontWeights.bold,
                padding:'15px'
              }}
            >
              Filters
      </Typography>
        <Button onClick={() => {
          // Reset selection to applied values when closing without applying
          setSelectedTask(appliedTask)
          setSelectedDepartment(appliedDepartment)
          setOpen(false)
        }}
        > <i style={{ color: 'gray' }} className="fa fa-close" ></i></Button>
      </Box>
      
      <Typography
              component="div"
              sx={{
                fontSize: typographyTokens.fontSizes.caption,
                fontWeight: typographyTokens.fontWeights.regular,
                padding:'15px'
              }}
            >
              Please select a task or department from the dropdowns to filter the list.
      </Typography>

      <Box>
      <Stack
        display="flex"
        direction="column"
        alignItems="left"
        spacing={4}
        sx={{ p: 4 }}
      >
        <Box>
          <Autocomplete
            options={sortedDepartments}
            getOptionLabel={(option: any) =>
              option ? option.name : ""
            }
            value={selectedDepartment}
            onChange={(_, newValue) => {
              setSelectedDepartment(newValue);
              // Clear task selection when department changes
              setSelectedTask(null);
            }}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.id === value?.id
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={textFieldSx}
                size="small"
                placeholder="Search by Department Name"
                label="Department"
              />
            )}
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
          />
        </Box>

        <Box>
          <Autocomplete
            options={
              selectedDepartment
                ? sortedTasks.filter((task: any) => task.departmentID === selectedDepartment.id)
                : sortedTasks
            }
            getOptionLabel={(option: any) =>
              option ? option.name : ""
            }
            value={selectedTask}
            onChange={(_, newValue) => {
              setSelectedTask(newValue);
            }}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.id === value?.id
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={textFieldSx}
                size="small"
                placeholder="Search by Task Name"
                label="Task"
              />
            )}
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
          />
        </Box>
        
        <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-end", px: 2 }}>
          <Button
            size="large"
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#2372B9',
              font: 'normal normal 600 16px/22px Open Sans',
            }}
            disabled={!selectedTask && !selectedDepartment}
            onClick={()=>{
              setAppliedTask(selectedTask)
              setAppliedDepartment(selectedDepartment)
              setIsFilter(1)
              setOpen(false)}}
          >
            Apply
          </Button>
        </Box>
      </Stack>
    </Box>
    </Box>
  );
  return (
    <Box sx={{ minHeight: '500px' }}>
      {(loadingData) && <SpinnerOverlay />}
      <Box sx={{flexDirection:'row', display:'flex', justifyContent:'space-between', paddingRight:'20px'}}>
      <Box>
        <AdminHeader title="Tasks" />
      </Box>
      
      {!isLoading && (
          <Box>
            <Box sx={{ display: 'flex', minWidthidth: '210px', justifyContent: 'space-between' }}>
              {isFilter>0 && 
              <Button
              variant="outlined"
              size="small"
              sx={{
                '&.MuiButton-outlined': {
                  border: '2px solid #2372B9',
                  color: '#2372B9',
                  textTransform: 'none',
                  fontWeight: typographyTokens.fontWeights.bold,
                  height: 'min-content',
                  alignSelf: 'center',
                  marginRight:'10px'
                },
              }}
              onClick={()=>{
                setSelectedTask(null)
                setSelectedDepartment(null)
                setAppliedTask(null)
                setAppliedDepartment(null)
                setIsFilter(0)
              }}
            //onClick={() => {}}
            >
              Reset Filter
            </Button>}
              <Button
                variant="outlined"
                size="small"
                sx={{
                  '&.MuiButton-outlined': {
                    border: '2px solid #2372B9',
                    color: '#2372B9',
                    textTransform: 'none',
                    fontWeight: typographyTokens.fontWeights.bold,
                    height: 'min-content',
                    alignSelf: 'center',
                    marginRight:'10px'
                  },
                }}
                onClick={toggleDrawer(true)}
              //onClick={() => {}}
              >
                <i className="fa-regular fa-filter" style={{paddingRight:'5px'}}></i>Filter Tasks
              </Button>
              <Drawer open={open} >
        {DrawerList}
      </Drawer>
              <AddButton
                tab="Tasks"
                onAdd={(tab: string) => {
                  setSelectedTaskTemplate({
                    ...initTaskTemplate,
                  });
                  setIsOpen(true);
                }}
              />
            </Box>
          </Box>
      )}
      </Box>
      
      <ModalWidget
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={`${selectedTaskTemplate.name === '' ? 'Add' : 'Edit'} Tasks`}
        children={
          <AddTask
            onClose={() => setIsOpen(false)}
            selectedTask={selectedTaskTemplate}
            templateTasksShortNameList = {TaskTemplates ? TaskTemplates?.map(task=>task.shortName) : []}
            setLoadingData = {setLoadingData}
          />
        }
      />
      {isLoading ? (
        <Box pt={10} sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress data-testid="loading-spinner" size={100} />
        </Box>
      ) : (
        <>
        {filteredList?.map(
          (taskTemplate: TaskTemplate, key: number, tasks: TaskTemplate[]) => {
            return (
              <TaskCard
                taskTemplate={taskTemplate}
                key={key}
                tasks={tasks}
                index={key}
                onEditTriggered={(e: TaskTemplate) => {
                  setSelectedTaskTemplate(e);
                  setIsOpen(true);
                }}
              />
            );
          }
        )}
        {/* <PageNavigation
            count={data?.count ?? 0}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          /> */}
        </>
      )}

     
    </Box>
  );
};

export default AdminTasks;
